import React from 'react';
import { Lightning, Fire, Trophy, Barbell } from '@phosphor-icons/react';
import { PHASE_INFO, QUOTES, WORKOUTS, getPhaseWeekRange } from '../data/programData';
import { getMuscleColor } from '../data/exerciseGuides';

export default function Dashboard({ user, week, day, phase, phaseInfo, workoutLogs, mealLogs, today, setWeek, setDay, getDateForWeekDay, customProgram }) {
  const quoteIdx = parseInt(today.replace(/-/g, '')) % QUOTES.length;
  const quote = QUOTES[quoteIdx];
  const isCustom = !!(customProgram?.phases);

  // Resolve today's workout from custom or protocol
  const todayWorkout = (() => {
    if (isCustom) {
      let weeksSoFar = 0;
      for (const p of customProgram.phases) {
        if (week <= weeksSoFar + (p.weeks || 12)) {
          return p.workouts?.[String(day)] || null;
        }
        weeksSoFar += p.weeks || 12;
      }
      return customProgram.phases[0]?.workouts?.[String(day)] || null;
    }
    return WORKOUTS[phase] ? WORKOUTS[phase][day] : null;
  })();

  // Resolve phase info for custom programs
  const currentPhaseInfo = (() => {
    if (isCustom) {
      let weeksSoFar = 0;
      for (const p of customProgram.phases) {
        if (week <= weeksSoFar + (p.weeks || 12)) {
          return { id: p.id, name: p.name, focus: p.focus, color: '#FF6B35', weeks: p.weeks };
        }
        weeksSoFar += p.weeks || 12;
      }
      return { id: 1, name: customProgram.phases[0]?.name || 'Custom', focus: customProgram.phases[0]?.focus || '', color: '#FF6B35', weeks: 12 };
    }
    return phaseInfo;
  })();

  let workoutCount = 0;
  let mealDays = 0;
  for (let d = 1; d <= 6; d++) {
    const date = getDateForWeekDay(week, d);
    if (workoutLogs[date] && workoutLogs[date].sets && workoutLogs[date].sets.length > 0) workoutCount++;
    if (mealLogs[date] && mealLogs[date].meals) mealDays++;
  }

  // Current month heatmap (starts at day 1 of current month)
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const todayDate = now.getDate();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay(); // 0=Sun
  const monthName = now.toLocaleString('en', { month: 'long' }).toUpperCase();

  const heatmap = [];
  // Pad empty cells for days before the 1st
  for (let i = 0; i < (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1); i++) {
    heatmap.push({ empty: true });
  }
  // Fill in actual days
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    let score = 0;
    if (d <= todayDate) {
      if (workoutLogs[dateStr] && workoutLogs[dateStr].sets && workoutLogs[dateStr].sets.length > 0) score++;
      if (mealLogs[dateStr] && mealLogs[dateStr].meals) score++;
    }
    const isFuture = d > todayDate;
    const isToday = d === todayDate;
    heatmap.push({ date: dateStr, score, dayNum: d, isFuture, isToday });
  }

  const level = Math.floor((user.xp || 0) / 250);

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="dashboard-page">
      {/* Header — personal, no user switcher */}
      <div className="glass rounded-xl p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-impact text-3xl sm:text-4xl text-white truncate" data-testid="dashboard-greeting">
              {user.name}
            </h1>
            <p className="text-data text-[9px] text-neutral-400 mt-0.5">
              {isCustom ? (customProgram.program_name || 'Custom Plan') : '48-Week Protocol'}
            </p>
          </div>
          <div className="glass-elevated rounded-lg px-2.5 py-1.5 flex-shrink-0 ml-2" data-testid="xp-badge">
            <span className="text-metric text-xs text-warning">LVL {level}</span>
            <span className="text-data text-[8px] text-neutral-500 ml-1">{user.xp || 0} XP</span>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
          {[
            { label: 'Weight', value: `${user.weight}`, unit: user.unit === 'imperial' ? 'lbs' : 'kg' },
            { label: 'BF%', value: `${user.bf}`, unit: '%' },
            { label: 'Week', value: `${week}`, unit: '/48' },
            { label: 'Day', value: `${day}`, unit: '/6' },
          ].map(s => (
            <div key={s.label} className="glass-elevated rounded-lg p-1.5 sm:p-2 text-center">
              <div className="text-data text-[8px] text-neutral-500">{s.label}</div>
              <div className="text-metric text-lg sm:text-xl text-white">{s.value}<span className="text-[10px] text-neutral-500">{s.unit}</span></div>
            </div>
          ))}
        </div>
      </div>

      {/* Phase indicator */}
      <div className="glass rounded-xl p-3 sm:p-4" style={{ borderLeftColor: currentPhaseInfo.color, borderLeftWidth: '4px' }}>
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="font-head text-base sm:text-xl font-bold uppercase text-white">
              {isCustom ? currentPhaseInfo.name : `PHASE ${phase}: ${currentPhaseInfo.name}`}
            </div>
            <div className="text-xs sm:text-sm text-neutral-400 mt-0.5 sm:mt-1">{currentPhaseInfo.focus}</div>
            {isCustom && customProgram.program_name && (
              <div className="text-[10px] text-accent font-bold uppercase mt-1">{customProgram.program_name}</div>
            )}
          </div>
          <Lightning size={28} weight="bold" className="flex-shrink-0 ml-2" style={{ color: currentPhaseInfo.color }} />
        </div>
      </div>

      {/* Today's Workout Preview */}
      {todayWorkout && (
        <div className="glass rounded-xl p-4" data-testid="todays-workout-preview">
          <div className="flex items-center gap-2 mb-3">
            <Barbell size={20} weight="bold" className="text-brand" />
            <span className="font-head text-base font-bold uppercase text-white">Today — {todayWorkout.name}</span>
          </div>
          {todayWorkout.warmup && (
            <div className="text-[10px] text-neutral-500 mb-2 uppercase">{todayWorkout.warmup}</div>
          )}
          <div className="space-y-1.5">
            {todayWorkout.exercises.slice(0, 5).map((ex, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: getMuscleColor(ex.muscle ? ex.muscle.split(' / ')[0] : '') }} />
                <span className="text-xs text-neutral-300 flex-1 truncate">{ex.name}</span>
                <span className="text-[10px] font-mono text-neutral-500">{ex.sets}x{ex.reps}</span>
              </div>
            ))}
            {todayWorkout.exercises.length > 5 && (
              <div className="text-[10px] text-neutral-600 italic">+{todayWorkout.exercises.length - 5} more exercises</div>
            )}
          </div>
          {todayWorkout.cardio && (
            <div className="mt-2 pt-2 border-t border-border text-[10px] text-neutral-500">
              <Fire size={10} weight="bold" className="inline text-brand mr-1" />{todayWorkout.cardio}
            </div>
          )}
        </div>
      )}

      {/* Phase pills */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {[1, 2, 3, 4].map(p => (
          <button
            key={p}
            onClick={() => { const [start] = getPhaseWeekRange(p); setWeek(start); }}
            className={`flex-shrink-0 px-4 py-3 rounded-md text-sm font-bold uppercase border transition-all active:scale-95 ${
              phase === p ? 'text-white border-transparent' : 'bg-elevated text-neutral-500 border-border'
            }`}
            style={phase === p ? { backgroundColor: PHASE_INFO[p].color } : {}}
            data-testid={`phase-pill-${p}`}
          >
            P{p} — {PHASE_INFO[p].name}
          </button>
        ))}
      </div>

      {/* Day chips */}
      <div className="grid grid-cols-6 gap-2">
        {[1, 2, 3, 4, 5, 6].map(d => {
          const date = getDateForWeekDay(week, d);
          const hasLog = workoutLogs[date] && workoutLogs[date].sets && workoutLogs[date].sets.length > 0;
          return (
            <button
              key={d} onClick={() => setDay(d)}
              className={`flex flex-col items-center py-3 rounded-md border text-sm font-bold transition-all active:scale-95 ${
                day === d ? 'bg-brand text-white border-brand'
                  : hasLog ? 'bg-success/10 text-success border-success/30'
                  : 'bg-elevated text-neutral-500 border-border'
              }`}
              data-testid={`day-chip-${d}`}
            >
              <span className="text-[10px] uppercase">Day</span>
              <span className="font-mono text-lg">{d}</span>
            </button>
          );
        })}
      </div>

      {/* Streak + Week done */}
      <div className="grid grid-cols-2 gap-3">
        <div className="glass rounded-xl p-4 flex items-center gap-3">
          <Fire size={32} weight="bold" className="text-warning" />
          <div>
            <div className="font-mono text-2xl font-bold text-warning" data-testid="streak-count">{user.streak_current || 0}</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted">Day Streak</div>
          </div>
        </div>
        <div className="glass rounded-xl p-4 flex items-center gap-3">
          <Trophy size={32} weight="bold" className="text-brand" />
          <div>
            <div className="font-mono text-2xl font-bold text-white">{workoutCount}/6</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted">Week Done</div>
          </div>
        </div>
      </div>

      {/* Feature 9: Weekly Volume Comparison */}
      {(() => {
        const thisWeekVol = Object.entries(workoutLogs).reduce((sum, [date, log]) => {
          const d = new Date(date);
          const weekStart = new Date(getDateForWeekDay(week, 1));
          const weekEnd = new Date(getDateForWeekDay(week, 6));
          if (d >= weekStart && d <= weekEnd && log.sets) {
            return sum + log.sets.reduce((s, set) => s + ((set.weight || 0) * (set.reps || 0)), 0);
          }
          return sum;
        }, 0);
        const lastWeekVol = Object.entries(workoutLogs).reduce((sum, [date, log]) => {
          const d = new Date(date);
          const weekStart = new Date(getDateForWeekDay(Math.max(1, week - 1), 1));
          const weekEnd = new Date(getDateForWeekDay(Math.max(1, week - 1), 6));
          if (d >= weekStart && d <= weekEnd && log.sets) {
            return sum + log.sets.reduce((s, set) => s + ((set.weight || 0) * (set.reps || 0)), 0);
          }
          return sum;
        }, 0);
        const change = lastWeekVol > 0 ? ((thisWeekVol - lastWeekVol) / lastWeekVol * 100).toFixed(1) : 0;
        if (thisWeekVol > 0 || lastWeekVol > 0) {
          return (
            <div className="glass rounded-xl p-4" data-testid="volume-comparison">
              <div className="flex items-center justify-between mb-2">
                <span className="font-head text-sm font-bold uppercase text-white">Weekly Volume</span>
                {lastWeekVol > 0 && (
                  <span className={`font-mono text-xs font-bold ${parseFloat(change) >= 0 ? 'text-success' : 'text-red-400'}`}>
                    {parseFloat(change) >= 0 ? '+' : ''}{change}%
                  </span>
                )}
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <div className="text-[9px] text-neutral-500 uppercase font-bold mb-1">This Week</div>
                  <div className="font-mono text-xl font-bold text-accent">{(thisWeekVol / 1000).toFixed(1)}t</div>
                </div>
                {lastWeekVol > 0 && (
                  <div className="flex-1 text-right">
                    <div className="text-[9px] text-neutral-500 uppercase font-bold mb-1">Last Week</div>
                    <div className="font-mono text-xl font-bold text-neutral-500">{(lastWeekVol / 1000).toFixed(1)}t</div>
                  </div>
                )}
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Adherence bars */}
      <div className="glass rounded-xl p-4 space-y-3">
        <div className="font-head text-lg font-bold uppercase text-white">Weekly Adherence</div>
        {[
          { label: 'Workouts', count: workoutCount, total: 6, color: '#FF3B30' },
          { label: 'Meals Tracked', count: mealDays, total: 6, color: '#007AFF' },
        ].map(bar => (
          <div key={bar.label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-neutral-400 uppercase font-bold tracking-wider">{bar.label}</span>
              <span className="font-mono text-white">{Math.round((bar.count / bar.total) * 100)}%</span>
            </div>
            <div className="h-2 bg-elevated rounded-sm overflow-hidden">
              <div className="h-full rounded-sm transition-all duration-500" style={{ width: `${(bar.count / bar.total) * 100}%`, backgroundColor: bar.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* Heatmap */}
      <div className="glass rounded-xl p-4">
        <div className="font-head text-lg font-bold uppercase text-white mb-3">{monthName} Activity</div>
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} className="text-center text-[9px] font-bold text-neutral-600 uppercase">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5" data-testid="activity-heatmap">
          {heatmap.map((h, i) => {
            if (h.empty) return <div key={i} />;
            return (
              <div key={i}
                className={`aspect-square rounded-sm flex items-center justify-center text-[10px] font-mono font-bold border ${
                  h.isToday && h.score > 0 ? 'ring-2 ring-success ring-offset-1 ring-offset-base' :
                  h.isToday ? 'ring-2 ring-brand ring-offset-1 ring-offset-base' : ''
                }`}
                style={{
                  backgroundColor: h.isFuture ? '#0A0A0A' : h.score === 0 ? '#0F0F0F' : h.score === 1 ? 'rgba(50,215,75,0.2)' : 'rgba(50,215,75,0.5)',
                  borderColor: h.isToday ? '#FF3B30' : h.score > 0 ? 'rgba(50,215,75,0.4)' : '#262626',
                  color: h.isFuture ? '#1A1A1A' : h.score > 0 ? '#32D74B' : '#525252',
                }}
                title={h.date}
              >{h.dayNum}</div>
            );
          })}
        </div>
      </div>

      {/* Quote */}
      <div className="bg-surface border-l-4 border-brand rounded-md p-4">
        <p className="text-neutral-300 italic text-sm leading-relaxed">"{quote}"</p>
      </div>
    </div>
  );
}
